import { AvatarProps } from '@amzn/cuminuicomponents';
import { ReactElement } from 'react';
import { Media } from 'src/utils/media';

export interface SearchBarContainerLayoutProps {
  avatar: ReactElement,
  searchInput: ReactElement,
  createDiscussion: ReactElement,
}

export type RenderCreateDiscussionButtonProps = { label: string, iconElement?: ReactElement };

export interface CreateDiscussionButtonLayoutProps {
  iconElement: ReactElement,
  label: string,
  labelCompact: string,
  renderCreateDiscussionButton: (props: RenderCreateDiscussionButtonProps) => ReactElement
}

export interface AvatarLayoutProps {
  renderlinkElement: (children: React.ReactNode) => ReactElement,
  AvatarElement: React.ComponentType<AvatarProps>,
  avatarSrc: string,
  label: string,
  isAuthenticated: boolean
}

export const CreateDiscussionButtonLayout = ({
  iconElement,
  label,
  labelCompact,
  renderCreateDiscussionButton,
}: CreateDiscussionButtonLayoutProps) => (
  <div>
    <Media between={['xs', 'md']}>
      {renderCreateDiscussionButton({ label: labelCompact })}
    </Media>
    <Media greaterThanOrEqual="md">
      {renderCreateDiscussionButton({ label, iconElement })}
    </Media>
  </div>
);

export const AvatarLayout = ({
  renderlinkElement,
  AvatarElement,
  avatarSrc,
  label,
  isAuthenticated,
}: AvatarLayoutProps) => {
  if (isAuthenticated) {
    return (
      <>
        <Media between={['xs', 'md']}>
          {renderlinkElement(<AvatarElement src={avatarSrc} size="md" />)}
        </Media>
        <Media greaterThanOrEqual="md">
          <div className="rounded py-1.5 hover:bg-kat-zircon ltr:pr-3.5 ltr:pl-2.5 rtl:pr-2.5 rtl:pl-3.5">
            {renderlinkElement(<AvatarElement src={avatarSrc} text={label} size="lg" />)}
          </div>
        </Media>
      </>
    );
  }
  return (
    <>
      <Media between={['xs', 'md']}>
        {renderlinkElement(label)}
      </Media>
      <Media greaterThanOrEqual="md">
        <div className="flex flex-row items-center space-x-3">
          <AvatarElement src={avatarSrc} size="lg" />
          {renderlinkElement(label)}
        </div>
      </Media>
    </>
  );
};

export const SearchBarContainerLayout = ({
  avatar,
  searchInput,
  createDiscussion,
}: SearchBarContainerLayoutProps) => (
  <div data-testid="search-bar">
    <Media between={['xs', 'md']}>
      <div data-testid="search-bar-mobile" className="bg-kat-azure py-1.5">
        <div
          className="flex w-full flex-row items-center px-[2.5%] pt-3 pb-1"
        >
          {avatar}
          <div className="mx-5 grow">
            {searchInput}
          </div>
          {createDiscussion}
        </div>
      </div>
    </Media>
    <Media greaterThanOrEqual="md">
      <div data-testid="search-bar-desktop" className="border border-solid border-kat-athens border-x-transparent bg-kat-snow py-1.5">
        <div className="kat-row items-center px-[2.5%]">
          <div className="kat-col-sm-3 flex ltr:pl-0 rtl:pr-0">
            {avatar}
          </div>
          <div className="kat-col-sm-6">
            {searchInput}
          </div>
          <div className="kat-col-sm-3 ltr:pr-0 rtl:pl-0">
            <div className="flex justify-end">
              {createDiscussion}
            </div>
          </div>
        </div>
      </div>
    </Media>
  </div>
);
